import { useCallback, useEffect, useRef } from "react";
import {
  useIsInViewport,
  type HandleInViewportChange,
} from "./useIsInViewport";

type UseImpressionEventProps = {
  ref: React.MutableRefObject<HTMLElement | null>;
  isInViewportCallback: () => void;
  isReady?: boolean;
  threshold?: number;
  delay?: number;
};

/**
 * This hook is used to trigger an impression event once the required conditions have been met.
 * Once triggered, the observer is disconnected so that only one impression event is fired.
 * @param ref The element to observe.
 * @param isInViewportCallback The function to call when the element is in the viewport and has met the delay and threshold conditions. After this has been triggered the observer is disconnected.
 * @param isReady A boolean indicating if the ad's data has finished loading and is eligible to start the impression event timout.
 * @param threshold (Default is 0.5) The minimum amount of the element that must be visible in the viewport, from 0-1.
 * @param delay (Default is 1000ms) The delay in milliseconds before the impression event is triggered.
 */

export function useImpressionEvent({
  ref,
  isInViewportCallback,
  isReady = true,
  threshold = 0.5,
  delay = 1000,
}: UseImpressionEventProps) {
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear the timer when the component unmounts.
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const handleInViewportChange: HandleInViewportChange = useCallback(
    async (isInViewport: boolean) => {
      if (isInViewport) {
        // An impression is only valid if it has been in the viewport
        // for a minimum of 1 second.
        return new Promise((resolve) => {
          const currentTimerId = setTimeout(() => {
            if (timerRef.current === currentTimerId) {
              isInViewportCallback();
              resolve("unobserve");
            }
          }, delay);
          timerRef.current = currentTimerId;
        });
      }

      if (timerRef.current) {
        // Clear the timer if the element leaves the viewport.
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      return Promise.resolve("observe");
    },
    [isInViewportCallback, delay],
  );

  useIsInViewport({
    ref: isReady ? ref : { current: null },
    handleInViewportChange,
    threshold,
  });
}
